// « Couverture Relief 3D · 79 MAD » on the order page — with a pencil.
//
// The owner, 2026-09-29: « let me be able to change the price of the 3D
// relief ». Pencil → number → Enregistrer. The worker
// (POST /orders/:id/relief-price) re-prices the order with the one pricing
// function, so the total, the courier's cash-to-collect and every payment
// message follow, and it writes the change as an order note.
//
// The rules the worker enforces, said here before the click where possible:
//   - 0 = « offert »: the cover is still embossed and the line stays.
//   - a negative (or empty) price cannot be saved.
//   - an admin's corrected total (« Prix corrigé manuellement ») wins: the
//     pencil explains instead of opening a field.
//   - a parcel already at AMEEX keeps its old amount until « Modifier » on the
//     AMEEX card sends the new one — the result says so.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ordersApi, type Order } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { usePermissions } from '@/lib/permissionsStore';
import { formatPrice } from '@/lib/utils';

type SaveResult = Awaited<ReturnType<typeof ordersApi.setReliefPrice>>;

export function ReliefPriceField({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const canEdit = can('orders.price_correction');

  const cost = Number(order.relief_3d_cost) || 0;
  const override = order.price_override != null && Number.isFinite(Number(order.price_override))
    ? Number(order.price_override)
    : null;

  const [mode, setMode] = useState<'view' | 'edit' | 'override'>('view');
  const [value, setValue] = useState(String(cost));
  const [result, setResult] = useState<SaveResult | null>(null);

  const n = Number(value);
  const empty = value.trim() === '';
  const negative = !empty && Number.isFinite(n) && n < 0;
  const invalid = empty || !Number.isFinite(n) || n < 0;

  const save = useMutation({
    mutationFn: (price: number) => ordersApi.setReliefPrice(shopifyOrderId, price),
    onSuccess: (res) => {
      setResult(res);
      setMode('view');
      // The line, the Prix total, the AMEEX card's default and the notes all
      // read these.
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-admin-notes', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  const open = () => {
    setResult(null);
    save.reset();
    if (override != null) { setMode('override'); return; }
    setValue(String(cost));
    setMode('edit');
  };

  return (
    <div>
      <dt className="text-xs text-gray-400 mb-0.5 flex items-center gap-1.5">
        {t('Couverture Relief 3D')}
        {canEdit && mode === 'view' && (
          <button
            type="button"
            onClick={open}
            aria-label={t('Modifier le prix du Relief 3D')}
            title={t('Modifier le prix du Relief 3D')}
            className="inline-flex items-center justify-center w-6 h-6 -my-1 rounded text-[13px] leading-none hover:bg-gray-100 text-adm-gold-ink"
          >
            ✎
          </button>
        )}
      </dt>
      <dd className="font-medium text-gray-800 text-sm">
        {mode === 'edit' ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !invalid && !save.isPending) save.mutate(Math.round(n)); }}
                  autoFocus
                  aria-label={t('Prix du Relief 3D (MAD)')}
                  className="w-24 pl-2.5 pr-10 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">MAD</span>
              </div>
              <Button
                size="sm"
                onClick={() => { if (!invalid) save.mutate(Math.round(n)); }}
                loading={save.isPending}
                disabled={invalid || save.isPending}
              >
                {t('Enregistrer')}
              </Button>
              <button
                type="button"
                onClick={() => { setMode('view'); save.reset(); }}
                disabled={save.isPending}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                {t('Annuler')}
              </button>
            </div>
            <p className={`text-[11px] mt-1 ${negative ? 'text-red-600' : 'text-gray-400'}`}>
              {negative ? t('Le prix ne peut pas être négatif (0 = offert).') : t('0 = offert')}
            </p>
          </>
        ) : (
          <span>{cost === 0 ? t('Offert (0 MAD)') : formatPrice(cost)}</span>
        )}

        {mode === 'override' && override != null && (
          <p className="text-[11px] mt-1 leading-snug text-amber-700">
            {t('Total corrigé à la main ({total} MAD) : il prime. Cliquez d’abord « Rétablir le prix automatique » pour changer le prix du Relief 3D.', { total: Math.round(override) })}
            {' '}
            <button type="button" onClick={() => setMode('view')} className="underline decoration-dotted hover:no-underline">
              {t('Fermer')}
            </button>
          </p>
        )}

        {result && mode === 'view' && !result.unchanged && (
          <>
            <p className="text-[11px] mt-1 text-green-600">
              ✓ {t('Enregistré · total {before} → {after} MAD', { before: Math.round(result.total_before), after: Math.round(result.total) })}
            </p>
            {result.ameex_parcel_open && (
              <p className="text-[11px] mt-1 leading-snug text-amber-700">
                ⚠ {t('Colis AMEEX déjà créé : cliquez « Modifier » sur la carte AMEEX puis « Enregistrer » pour lui envoyer le nouveau montant.')}
              </p>
            )}
          </>
        )}
        {save.isError && (
          <p className="text-[11px] mt-1 text-red-600">
            {(save.error as Error)?.message ?? t('Échec de la correction')}
          </p>
        )}
      </dd>
    </div>
  );
}
